import { useState, useEffect } from "react";
import ArrowCircleRightIcon from "@mui/icons-material/ArrowCircleRight";
import {
    Tabs,
    Tab,
    CircularProgress,
    Button,
    Grid,
    Box,
    useTheme,
    useMediaQuery,
} from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

import { request } from "@/Api";
import ApiAutocomplete from "@/common/ApiAutocomplete";
import { CustomBox } from "@/common/StyledComponents";
import { Subtitle, SectionTitle, ErrorMessage } from "@/common/typography";
import { selectBidPopupStatus, closeBidPopup } from "@/nav/navSlice";

export default function BidPopup() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));

    const navigate = useNavigate();
    const dispatch = useDispatch();
    const popupStatus = useSelector(selectBidPopupStatus);

    const bidPopupOpenType = popupStatus.type;

    const [projectId, setProjectId] = useState(popupStatus.projectId);
    const [popupState, setPopupState] = useState("templates");
    const [template, setTemplate] = useState(null);
    const [error, setError] = useState(null);

    const createBidFromTemplate = () => {
        setPopupState("loading");

        request
            .post("/bids/copy", {
                bid_name: template.name,
                project_id: projectId,
                bid_id: template.bid_id,
                is_change_order: bidPopupOpenType == "Change Order",
                public: template.public,
            })
            .catch(() => {
                setError(
                    "There was a problem creating a bid sheet. Please try again or contact support.",
                );
            })
            .then((response) => {
                dispatch(closeBidPopup());
                navigate(`/bids/${response.data.id}`);
            });
    };

    useEffect(() => {
        if (!template) {
            return;
        }

        if (projectId) {
            createBidFromTemplate();
        } else {
            setPopupState("projects");
        }
    }, [template]);

    return (
        <CustomBox
            width={isMobile ? "90%" : "80%"}
            padding={isMobile ? "24px 12px" : "24px"}
            height={isMobile ? "90vh" : "80vh"}
            sx={{ background: theme.palette.background.default, outline: 0 }}
        >
            {popupState == "loading" ? (
                <Grid
                    container
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                    gap={2}
                    sx={{ height: "100%" }}
                >
                    {error ? (
                        <Grid item>
                            <ErrorMessage>{error}</ErrorMessage>
                        </Grid>
                    ) : (
                        <>
                            <Grid item>
                                <CircularProgress />
                            </Grid>
                            <Grid item>
                                <Subtitle>Creating bid sheet...</Subtitle>
                            </Grid>
                        </>
                    )}
                </Grid>
            ) : popupState == "templates" ? (
                <BidPopupTemplates
                    theme={theme}
                    bidPopupOpenType={bidPopupOpenType}
                    setTemplate={setTemplate}
                    projectId={projectId}
                    closeBidPopup={() => dispatch(closeBidPopup())}
                />
            ) : popupState == "projects" ? (
                <>
                    <SectionTitle margin="0 24px 0 0">
                        Select Project
                    </SectionTitle>
                    <ApiAutocomplete
                        value={null}
                        apiRoute={"projects"}
                        fields={["name"]}
                        onChange={(event, newValue) =>
                            setProjectId(newValue.id)
                        }
                        label="Project"
                        simple={true}
                        fullOptionData
                    />
                </>
            ) : null}

            <Grid
                container
                xs={12}
                sm={6}
                md={4}
                columnSpacing={2}
                justifyContent="flex-end"
                sx={{
                    position: "absolute",
                    bottom: "24px",
                    right: "24px",
                    zIndex: 100,
                }}
            >
                {(popupState != "loading" || error) && (
                    <Grid item>
                        <Button
                            variant="contained"
                            color="secondary"
                            onClick={() => dispatch(closeBidPopup())}
                        >
                            Cancel
                        </Button>
                    </Grid>
                )}
                {popupState == "projects" && (
                    <Grid item>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={createBidFromTemplate}
                            data-testid="create-bid-submit"
                        >
                            Create
                        </Button>
                    </Grid>
                )}
            </Grid>
        </CustomBox>
    );
}

const BidPopupTemplates = ({
    theme,
    bidPopupOpenType,
    setTemplate,
    projectId,
    closeBidPopup,
}) => {
    const [bidTemplates, setBidTemplates] = useState({});
    const [tabValue, setTabValue] = useState(0);

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    const getBidTemplates = () => {
        request
            .get("/bid-templates", { params: { set: "active" } })
            .then((response) => {
                const bid_templates_raw = response.data;
                setBidTemplates(
                    bid_templates_raw.reduce((acc, template) => {
                        const tab = template.public
                            ? "Public"
                            : template.person_id
                              ? "My Templates"
                              : "Workspace";
                        const category = template.category || "Custom";
                        acc[tab] = acc[tab] || {};
                        acc[tab][category] = acc[tab][category] || [];
                        acc[tab][category].push(template);
                        return acc;
                    }, {}),
                );
            });
    };

    useEffect(() => {
        getBidTemplates();
    }, []);

    return (
        <div
            style={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                paddingBottom: "60px",
                boxSizing: "border-box",
            }}
        >
            <SectionTitle>Choose a Template</SectionTitle>
            <Subtitle>{`to create a ${bidPopupOpenType}`}</Subtitle>

            <Tabs
                value={tabValue}
                onChange={handleTabChange}
                sx={{ marginTop: "16px" }}
            >
                {Object.entries(bidTemplates).map((type) => {
                    return <Tab label={type[0]} key={type[0]} />;
                })}
            </Tabs>

            {Object.entries(bidTemplates).map((type, index) => (
                <div
                    hidden={index != tabValue}
                    key={index}
                    style={{ overflow: "auto" }}
                >
                    {Object.entries(type[1]).map((category) => (
                        <div key={category[0]}>
                            <p>{category[0]}</p>
                            <Grid container spacing={2}>
                                {category[1].map((template) => (
                                    <Grid item key={template.id}>
                                        <Box
                                            borderRadius={5}
                                            sx={{
                                                background:
                                                    theme.palette.background
                                                        .paper,
                                                height: 150,
                                                width: 170,
                                                cursor: "pointer",
                                            }}
                                            onClick={() =>
                                                setTemplate(template)
                                            }
                                            data-testid={`bid-template-${template.id}`}
                                        >
                                            <div style={{ padding: 30 }}>
                                                <ArrowCircleRightIcon fontSize="large" />
                                                <br />
                                                {template.name}
                                                <br />
                                            </div>
                                        </Box>
                                    </Grid>
                                ))}
                            </Grid>
                        </div>
                    ))}
                </div>
            ))}
        </div>
    );
};
